import React, { useCallback, useEffect, useState } from 'react';
import {
  QrCode, Activity, Users, Bot, Zap, Tag, BarChart3, Trash2, Plus, Save, Wifi, WifiOff, Moon, BellRing,
} from 'lucide-react';
import { jarvis, ESTADOS, formatarTelefone, tempoDesde } from './api';
import Relatorios from './Relatorios';
import AvisosWhatsapp from './AvisosWhatsapp';

const ABAS = [
  ['conexao', 'Conexão', QrCode],
  ['monitor', 'Monitoramento', Activity],
  ['filas', 'Filas e atendentes', Users],
  ['robo', 'Robô e horários', Bot],
  ['textos', 'Textos prontos', Zap],
  ['categorias', 'Categorias', Tag],
  ['relatorios', 'Relatórios', BarChart3],
  ['avisos', 'Avisos WhatsApp', BellRing],
];

export default function Supervisor({ eu, voltar }) {
  const [aba, setAba] = useState('conexao');
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const avisar = useCallback(m => { setOk(m); setTimeout(() => setOk(''), 2500); }, []);
  const props = { setErro, avisar };
  return (
    <div className="jv-supervisor">
      <div className="jv-barra">{voltar}<strong>Supervisor</strong></div>
      <div className="jv-sup-corpo">
        <nav className="jv-sup-menu">
          {ABAS.map(([k, r, Icone]) => (
            <button key={k} className={aba === k ? 'ativa' : ''} onClick={() => { setAba(k); setErro(''); }}><Icone size={17} /> {r}</button>
          ))}
        </nav>
        <main className="jv-sup-conteudo">
          {erro && <div className="jv-aviso jv-aviso-erro">{erro}</div>}
          {ok && <div className="jv-aviso jv-aviso-ok">{ok}</div>}
          {aba === 'conexao' && <Conexao {...props} />}
          {aba === 'monitor' && <Monitor {...props} />}
          {aba === 'filas' && <Filas {...props} />}
          {aba === 'robo' && <Robo {...props} />}
          {aba === 'textos' && <Textos {...props} />}
          {aba === 'categorias' && <Categorias {...props} />}
          {aba === 'relatorios' && <Relatorios {...props} />}
          {aba === 'avisos' && <AvisosWhatsapp {...props} />}
        </main>
      </div>
    </div>
  );
}

// ── Conexão (QR code) ─────────────────────────────────────────────────────────
function Conexao({ setErro }) {
  const [estado, setEstado] = useState(null);
  const [qr, setQr] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const verEstado = useCallback(() => jarvis('conexao').then(r => { setEstado(r); if (r.estado === 'open') setQr(null); }).catch(e => setErro(e.message)), [setErro]);
  useEffect(() => { verEstado(); const t = setInterval(verEstado, qr ? 3000 : 10000); return () => clearInterval(t); }, [verEstado, qr]);

  const conectar = useCallback(async () => {
    setOcupado(true); setErro('');
    try {
      const r = await jarvis('conectar');
      if (r.conectado) { setQr(null); verEstado(); } else setQr(r.qr);
    } catch (e) { setErro(e.message); }
    setOcupado(false);
  }, [setErro, verEstado]);
  // O QR do WhatsApp vence em ~40 s: renova sozinho enquanto está na tela
  useEffect(() => { if (!qr) return; const t = setTimeout(conectar, 35000); return () => clearTimeout(t); }, [qr, conectar]);

  const desconectar = async () => {
    if (!window.confirm('Desconectar o WhatsApp do Jarvis? As conversas param de chegar até conectar de novo.')) return;
    setOcupado(true);
    try { await jarvis('desconectar'); await verEstado(); } catch (e) { setErro(e.message); }
    setOcupado(false);
  };

  const conectado = estado?.estado === 'open';
  return (
    <div className="jv-cartao">
      <h3>Conexão com o WhatsApp</h3>
      <div className={`jv-status-conexao ${conectado ? 'on' : 'off'}`}>
        {conectado ? <Wifi size={22} /> : <WifiOff size={22} />}
        <div>
          <strong>{conectado ? 'Conectado' : estado?.estado === 'connecting' ? 'Aguardando leitura do QR code' : estado?.estado === 'nao_criada' ? 'Ainda não conectado' : 'Desconectado'}</strong>
          {conectado && estado.numero && <span>Número: {estado.numero}</span>}
        </div>
      </div>
      {!conectado && !qr && <button className="jv-btn jv-btn-primario" disabled={ocupado} onClick={conectar}><QrCode size={16} /> {ocupado ? 'Gerando…' : 'Gerar QR code'}</button>}
      {/* com o WhatsApp conectado: reaplica a ligação com o Quality (eventos do webhook, ex.: confirmação de entrega dos avisos) sem desconectar */}
      {conectado && <button className="jv-btn" disabled={ocupado} onClick={async () => { await conectar(); setErro(''); alert('Ligação com o Quality atualizada (inclui a confirmação de entrega e leitura dos avisos).'); }} title="Não desconecta o WhatsApp">↻ Atualizar ligação com o Quality</button>}
      {qr && (
        <div className="jv-qr">
          <img src={qr} alt="QR code do WhatsApp" />
          <ol>
            <li>No celular do chip do Jarvis, abra o <b>WhatsApp Business</b>.</li>
            <li>Toque em <b>⋮ (ou Configurações) → Dispositivos conectados → Conectar dispositivo</b>.</li>
            <li>Aponte a câmera para este QR code. Ele se renova sozinho a cada ~35 s.</li>
          </ol>
        </div>
      )}
      {conectado && <button className="jv-btn jv-btn-perigo" disabled={ocupado} onClick={desconectar}><WifiOff size={16} /> Desconectar</button>}
      <p className="jv-sub jv-dica">Use só este número para o atendimento e evite mandar a mesma mensagem para muita gente de uma vez — conexões por QR code podem ser bloqueadas pelo WhatsApp se parecerem robô de propaganda.</p>
    </div>
  );
}

// ── Monitoramento ao vivo ─────────────────────────────────────────────────────
function Monitor({ setErro }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    const ler = () => { if (document.visibilityState !== 'hidden') jarvis('monitor').then(setD).catch(e => setErro(e.message)); };
    ler(); const t = setInterval(ler, 5000); return () => clearInterval(t);
  }, [setErro]);
  if (!d) return <div className="jv-carregando">Carregando…</div>;
  const nomeFila = id => d.filas.find(f => f.id === id)?.nome || 'Sem fila';
  const porFila = {};
  d.abertas.forEach(c => { const k = c.fila_id || '-'; porFila[k] = porFila[k] || { bot: 0, espera: 0, atendimento: 0, maisAntiga: null }; porFila[k][c.estado]++; if (c.estado === 'espera' && (!porFila[k].maisAntiga || c.criado_em < porFila[k].maisAntiga)) porFila[k].maisAntiga = c.criado_em; });
  const porAtendente = {};
  d.abertas.filter(c => c.estado === 'atendimento').forEach(c => { porAtendente[c.atendente_email] = (porAtendente[c.atendente_email] || 0) + 1; });
  return (
    <>
      <div className="jv-numeros">
        <div><b>{d.abertas.filter(c => c.estado === 'espera').length}</b><span>aguardando</span></div>
        <div><b>{d.abertas.filter(c => c.estado === 'atendimento').length}</b><span>em atendimento</span></div>
        <div><b>{d.abertas.filter(c => c.estado === 'bot').length}</b><span>no robô</span></div>
        <div><b>{d.online.length}</b><span>atendentes online</span></div>
        <div><b>{d.hoje.length}</b><span>recebidas hoje</span></div>
        <div><b>{d.hoje.filter(c => c.estado === 'encerrada').length}</b><span>encerradas hoje</span></div>
      </div>
      <div className="jv-grade-2">
        <div className="jv-cartao">
          <h3>Por fila</h3>
          <table className="jv-tabela"><thead><tr><th>Fila</th><th>Aguardando</th><th>Mais antiga</th><th>Em atendimento</th></tr></thead>
            <tbody>
              {Object.keys(porFila).length === 0 && <tr><td colSpan={4} className="jv-sub">Nenhuma conversa aberta.</td></tr>}
              {Object.entries(porFila).map(([k, v]) => <tr key={k}><td>{nomeFila(k === '-' ? null : k)}</td><td>{v.espera}</td><td>{v.maisAntiga ? tempoDesde(v.maisAntiga) : '—'}</td><td>{v.atendimento}</td></tr>)}
            </tbody></table>
        </div>
        <div className="jv-cartao">
          <h3>Atendentes online</h3>
          <table className="jv-tabela"><thead><tr><th>Atendente</th><th>Conversas</th></tr></thead>
            <tbody>
              {d.online.length === 0 && <tr><td colSpan={2} className="jv-sub">Ninguém com a tela de operador aberta.</td></tr>}
              {d.online.map(a => <tr key={a.email}><td>● {a.nome}</td><td>{porAtendente[a.email] || 0}</td></tr>)}
            </tbody></table>
        </div>
      </div>
      <div className="jv-cartao">
        <h3>Conversas abertas</h3>
        <table className="jv-tabela"><thead><tr><th>Protocolo</th><th>Contato</th><th>Fila</th><th>Situação</th><th>Atendente</th><th>Aberta há</th></tr></thead>
          <tbody>
            {d.abertas.map(c => <tr key={c.id}><td>{c.protocolo}</td><td>{c.nome || formatarTelefone(c.telefone)}</td><td>{nomeFila(c.fila_id)}</td><td style={{ color: ESTADOS[c.estado]?.cor }}>{ESTADOS[c.estado]?.rotulo}{c.fora_horario ? ' 🌙' : ''}</td><td>{c.atendente_nome || '—'}</td><td>{tempoDesde(c.criado_em)}</td></tr>)}
          </tbody></table>
      </div>
    </>
  );
}

// ── Filas e atendentes ────────────────────────────────────────────────────────
function Filas({ setErro, avisar }) {
  const [filas, setFilas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [editando, setEditando] = useState(null);
  const [buscaU, setBuscaU] = useState('');
  useEffect(() => {
    jarvis('filasTodas').then(r => setFilas(r.filas)).catch(e => setErro(e.message));
    jarvis('usuarios').then(r => setUsuarios(r.usuarios)).catch(e => setErro(e.message));
  }, [setErro]);
  const salvar = async () => {
    try { const r = await jarvis('salvarFila', { fila: editando }); setFilas(r.filas); setEditando(null); avisar('Fila salva.'); } catch (e) { setErro(e.message); }
  };
  const apagar = async f => {
    if (!window.confirm(`Apagar a fila "${f.nome}"? As conversas dela ficam "sem fila".`)) return;
    try { const r = await jarvis('apagarFila', { id: f.id }); setFilas(r.filas); } catch (e) { setErro(e.message); }
  };
  const nome = email => usuarios.find(u => u.email === email)?.nome || email;
  const alternar = email => setEditando(f => ({ ...f, atendentes: f.atendentes.includes(email) ? f.atendentes.filter(x => x !== email) : [...f.atendentes, email] }));
  const visiveis = usuarios.filter(u => !buscaU || (u.nome + u.email).toLowerCase().includes(buscaU.toLowerCase()));

  return (
    <div className="jv-cartao">
      <div className="jv-cartao-cab"><h3>Filas de atendimento</h3><button className="jv-btn jv-btn-primario" onClick={() => setEditando({ nome: '', cor: '#42796e', ordem: filas.length + 1, ativa: true, atendentes: [] })}><Plus size={16} /> Nova fila</button></div>
      <table className="jv-tabela"><thead><tr><th>Ordem</th><th>Fila</th><th>Atendentes</th><th>Ativa</th><th /></tr></thead>
        <tbody>
          {filas.length === 0 && <tr><td colSpan={5} className="jv-sub">Nenhuma fila ainda. Crie as filas e depois monte o menu do robô na aba "Robô e horários".</td></tr>}
          {filas.map(f => (
            <tr key={f.id}>
              <td>{f.ordem}</td>
              <td><span className="jv-bolinha" style={{ background: f.cor || '#42796e' }} /> {f.nome}</td>
              <td>{(f.atendentes || []).map(nome).join(', ') || <span className="jv-sub">ninguém</span>}</td>
              <td>{f.ativa ? 'Sim' : 'Não'}</td>
              <td className="jv-acoes-linha"><button className="jv-btn" onClick={() => setEditando({ ...f, atendentes: f.atendentes || [] })}>Editar</button><button className="jv-icone" onClick={() => apagar(f)}><Trash2 size={16} /></button></td>
            </tr>
          ))}
        </tbody></table>

      {editando && (
        <div className="jv-modal-fundo" onMouseDown={() => setEditando(null)}>
          <div className="jv-modal jv-modal-g" onMouseDown={e => e.stopPropagation()}>
            <div className="jv-modal-cab"><strong>{editando.id ? 'Editar fila' : 'Nova fila'}</strong></div>
            <div className="jv-linha-campos">
              <label className="jv-campo">Nome<input value={editando.nome} onChange={e => setEditando({ ...editando, nome: e.target.value })} /></label>
              <label className="jv-campo jv-campo-p">Ordem<input type="number" value={editando.ordem} onChange={e => setEditando({ ...editando, ordem: e.target.value })} /></label>
              <label className="jv-campo jv-campo-p">Cor<input type="color" value={editando.cor || '#42796e'} onChange={e => setEditando({ ...editando, cor: e.target.value })} /></label>
            </div>
            <label className="jv-check"><input type="checkbox" checked={editando.ativa} onChange={e => setEditando({ ...editando, ativa: e.target.checked })} /> Fila ativa</label>
            <h4 className="jv-modal-sub">Quem atende esta fila ({editando.atendentes.length})</h4>
            <input className="jv-input-busca" placeholder="Buscar usuário do Quality…" value={buscaU} onChange={e => setBuscaU(e.target.value)} />
            <div className="jv-lista-check">
              {visiveis.map(u => (
                <label key={u.email} className="jv-check">
                  <input type="checkbox" checked={editando.atendentes.includes(u.email)} onChange={() => alternar(u.email)} />
                  {u.nome} <span className="jv-sub">{u.email}{!u.temAcesso && ' · sem permissão "Zaping"'}</span>
                </label>
              ))}
            </div>
            <p className="jv-sub">Para a pessoa abrir o Zaping, libere o módulo <b>Zaping</b> em Configurações → Usuários (Ver = operador, Ver e editar = supervisor).</p>
            <div className="jv-modal-rodape"><button className="jv-btn" onClick={() => setEditando(null)}>Cancelar</button><button className="jv-btn jv-btn-primario" onClick={salvar}><Save size={16} /> Salvar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Robô de entrada, horários, feriados, sobreaviso ───────────────────────────
const DIAS = [['seg', 'Segunda'], ['ter', 'Terça'], ['qua', 'Quarta'], ['qui', 'Quinta'], ['sex', 'Sexta'], ['sab', 'Sábado'], ['dom', 'Domingo']];

function Robo({ setErro, avisar }) {
  const [cfg, setCfg] = useState(null);
  const [filas, setFilas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    jarvis('config').then(r => setCfg(r.config)).catch(e => setErro(e.message));
    jarvis('filasTodas').then(r => setFilas(r.filas.filter(f => f.ativa))).catch(() => {});
  }, [setErro]);
  if (!cfg) return <div className="jv-carregando">Carregando…</div>;
  const mudar = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const opcoes = cfg.opcoes || [];
  const salvar = async () => {
    setSalvando(true);
    try { const r = await jarvis('salvarConfig', { config: cfg }); setCfg(r.config); avisar('Configurações salvas — o robô já usa as novas.'); } catch (e) { setErro(e.message); }
    setSalvando(false);
  };
  const previa = [cfg.boasVindas, [cfg.menuTexto, ...opcoes.map(o => `*${o.tecla}* - ${o.rotulo || filas.find(f => f.id === o.filaId)?.nome || '?'}`)].filter(Boolean).join('\n')].filter(Boolean).join('\n\n');

  return (
    <>
      <div className="jv-cartao">
        <div className="jv-cartao-cab"><h3>Robô de entrada</h3><button className="jv-btn jv-btn-primario" disabled={salvando} onClick={salvar}><Save size={16} /> {salvando ? 'Salvando…' : 'Salvar tudo'}</button></div>
        <div className="jv-grade-2">
          <div>
            <label className="jv-campo">Mensagem de boas-vindas<textarea rows={3} value={cfg.boasVindas || ''} onChange={e => mudar('boasVindas', e.target.value)} /></label>
            <label className="jv-campo">Texto antes do menu<input value={cfg.menuTexto || ''} onChange={e => mudar('menuTexto', e.target.value)} /></label>
            <h4 className="jv-modal-sub">Opções do menu</h4>
            {opcoes.map((o, i) => (
              <div key={i} className="jv-linha-campos">
                <input className="jv-campo-tecla" value={o.tecla} placeholder="1" onChange={e => mudar('opcoes', opcoes.map((x, j) => (j === i ? { ...x, tecla: e.target.value } : x)))} />
                <select value={o.filaId || ''} onChange={e => mudar('opcoes', opcoes.map((x, j) => (j === i ? { ...x, filaId: e.target.value } : x)))}>
                  <option value="">Fila…</option>
                  {filas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
                <input placeholder="Texto da opção (opcional)" value={o.rotulo || ''} onChange={e => mudar('opcoes', opcoes.map((x, j) => (j === i ? { ...x, rotulo: e.target.value } : x)))} />
                <button className="jv-icone" onClick={() => mudar('opcoes', opcoes.filter((_, j) => j !== i))}><Trash2 size={16} /></button>
              </div>
            ))}
            <button className="jv-btn" onClick={() => mudar('opcoes', [...opcoes, { tecla: String(opcoes.length + 1), filaId: '', rotulo: '' }])}><Plus size={16} /> Adicionar opção</button>
            {filas.length === 0 && <p className="jv-sub">Crie as filas primeiro (aba "Filas e atendentes"). Sem opções, toda conversa vai direto para a espera.</p>}
            <label className="jv-campo">Depois de escolher<textarea rows={2} value={cfg.msgAguarde || ''} onChange={e => mudar('msgAguarde', e.target.value)} /></label>
            <label className="jv-campo">Resposta inválida<textarea rows={2} value={cfg.msgOpcaoInvalida || ''} onChange={e => mudar('msgOpcaoInvalida', e.target.value)} /></label>
            <label className="jv-campo">Encerramento (use {'{protocolo}'})<textarea rows={2} value={cfg.msgEncerramento || ''} onChange={e => mudar('msgEncerramento', e.target.value)} /></label>
          </div>
          <div>
            <h4 className="jv-modal-sub">Prévia do que o contato recebe</h4>
            <div className="jv-previa">{previa}</div>
          </div>
        </div>
      </div>

      <div className="jv-cartao">
        <h3>Horário de atendimento e sobreaviso</h3>
        <table className="jv-tabela jv-tabela-horario"><thead><tr><th>Dia</th><th>Atende</th><th>Das</th><th>Até</th></tr></thead>
          <tbody>
            {DIAS.map(([k, r]) => {
              const h = cfg.horario?.[k] || { ativo: false, ini: '08:00', fim: '18:00' };
              const set = v => mudar('horario', { ...cfg.horario, [k]: { ...h, ...v } });
              return <tr key={k}><td>{r}</td><td><input type="checkbox" checked={!!h.ativo} onChange={e => set({ ativo: e.target.checked })} /></td><td><input type="time" value={h.ini} disabled={!h.ativo} onChange={e => set({ ini: e.target.value })} /></td><td><input type="time" value={h.fim} disabled={!h.ativo} onChange={e => set({ fim: e.target.value })} /></td></tr>;
            })}
          </tbody></table>
        <label className="jv-campo"><span><Moon size={14} /> Fora do horário, a conversa vai para a fila</span>
          <select value={cfg.filaSobreaviso || ''} onChange={e => mudar('filaSobreaviso', e.target.value || null)}>
            <option value="">Sem fila (fica na espera geral)</option>
            {filas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </label>
        <label className="jv-campo">Mensagem fora do horário<textarea rows={2} value={cfg.msgForaHorario || ''} onChange={e => mudar('msgForaHorario', e.target.value)} /></label>
        <Feriados feriados={cfg.feriados || []} mudar={v => mudar('feriados', v)} />
        <button className="jv-btn jv-btn-primario" disabled={salvando} onClick={salvar}><Save size={16} /> Salvar tudo</button>
      </div>
    </>
  );
}

function Feriados({ feriados, mudar }) {
  const [data, setData] = useState('');
  const [nome, setNome] = useState('');
  const lista = feriados.map(f => (typeof f === 'string' ? { data: f, nome: '' } : f)).sort((a, b) => a.data.localeCompare(b.data));
  return (
    <div>
      <h4 className="jv-modal-sub">Feriados (dia inteiro fora do horário)</h4>
      <div className="jv-linha-campos">
        <input type="date" value={data} onChange={e => setData(e.target.value)} />
        <input placeholder="Nome (opcional)" value={nome} onChange={e => setNome(e.target.value)} />
        <button className="jv-btn" disabled={!data} onClick={() => { mudar([...lista.filter(f => f.data !== data), { data, nome }]); setData(''); setNome(''); }}><Plus size={16} /> Adicionar</button>
      </div>
      <div className="jv-chips">
        {lista.map(f => <span key={f.data} className="jv-chip">{f.data.split('-').reverse().join('/')} {f.nome}<button onClick={() => mudar(lista.filter(x => x.data !== f.data))}>×</button></span>)}
      </div>
    </div>
  );
}

// ── Textos prontos ────────────────────────────────────────────────────────────
function Textos({ setErro, avisar }) {
  const [textos, setTextos] = useState([]);
  const [filas, setFilas] = useState([]);
  const [ed, setEd] = useState(null);
  useEffect(() => {
    jarvis('textos').then(r => setTextos(r.textos)).catch(e => setErro(e.message));
    jarvis('filasTodas').then(r => setFilas(r.filas)).catch(() => {});
  }, [setErro]);
  const salvar = async () => { try { const r = await jarvis('salvarTexto', { texto: ed }); setTextos(r.textos); setEd(null); avisar('Texto salvo.'); } catch (e) { setErro(e.message); } };
  const apagar = async t => { if (!window.confirm(`Apagar "${t.titulo}"?`)) return; try { const r = await jarvis('apagarTexto', { id: t.id }); setTextos(r.textos); } catch (e) { setErro(e.message); } };
  return (
    <div className="jv-cartao">
      <div className="jv-cartao-cab"><h3>Textos prontos</h3><button className="jv-btn jv-btn-primario" onClick={() => setEd({ titulo: '', texto: '', fila_id: '' })}><Plus size={16} /> Novo texto</button></div>
      {ed && (
        <div className="jv-form-inline">
          <div className="jv-linha-campos">
            <label className="jv-campo">Título<input value={ed.titulo} onChange={e => setEd({ ...ed, titulo: e.target.value })} /></label>
            <label className="jv-campo">Fila<select value={ed.fila_id || ''} onChange={e => setEd({ ...ed, fila_id: e.target.value })}><option value="">Todas as filas</option>{filas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}</select></label>
          </div>
          <label className="jv-campo">Texto<textarea rows={4} value={ed.texto} onChange={e => setEd({ ...ed, texto: e.target.value })} /></label>
          <div className="jv-modal-rodape"><button className="jv-btn" onClick={() => setEd(null)}>Cancelar</button><button className="jv-btn jv-btn-primario" onClick={salvar}><Save size={16} /> Salvar</button></div>
        </div>
      )}
      <table className="jv-tabela"><thead><tr><th>Título</th><th>Fila</th><th>Texto</th><th /></tr></thead>
        <tbody>
          {textos.length === 0 && <tr><td colSpan={4} className="jv-sub">Nenhum texto pronto.</td></tr>}
          {textos.map(t => <tr key={t.id}><td>{t.titulo}</td><td>{filas.find(f => f.id === t.fila_id)?.nome || 'Todas'}</td><td className="jv-td-texto">{t.texto}</td><td className="jv-acoes-linha"><button className="jv-btn" onClick={() => setEd({ ...t, fila_id: t.fila_id || '' })}>Editar</button><button className="jv-icone" onClick={() => apagar(t)}><Trash2 size={16} /></button></td></tr>)}
        </tbody></table>
    </div>
  );
}

// ── Categorias de encerramento ────────────────────────────────────────────────
function Categorias({ setErro }) {
  const [cats, setCats] = useState([]);
  const [nova, setNova] = useState('');
  useEffect(() => { jarvis('categorias').then(r => setCats(r.categorias)).catch(e => setErro(e.message)); }, [setErro]);
  const criar = async () => { try { const r = await jarvis('salvarCategoria', { nome: nova, ordem: cats.length + 1 }); setCats(r.categorias); setNova(''); } catch (e) { setErro(e.message); } };
  const apagar = async c => { try { const r = await jarvis('apagarCategoria', { id: c.id }); setCats(r.categorias); } catch (e) { setErro(e.message); } };
  return (
    <div className="jv-cartao">
      <h3>Categorias de encerramento</h3>
      <p className="jv-sub">O operador escolhe uma ao encerrar — vira o "motivo" no relatório (ex.: Resolvido, Encerrado com pendência da escala, Sem resposta do contato).</p>
      <div className="jv-linha-campos">
        <input placeholder="Nova categoria…" value={nova} onChange={e => setNova(e.target.value)} onKeyDown={e => e.key === 'Enter' && nova.trim() && criar()} />
        <button className="jv-btn jv-btn-primario" disabled={!nova.trim()} onClick={criar}><Plus size={16} /> Adicionar</button>
      </div>
      <div className="jv-chips">{cats.map(c => <span key={c.id} className="jv-chip">{c.nome}<button onClick={() => apagar(c)}>×</button></span>)}</div>
    </div>
  );
}
